import { t } from "@lingui/macro";
import {
  type CreateScopedTaskMutationVariables,
  type CreateTaskMutationVariables,
  useCreateScopedTaskMutation,
  useCreateTaskMutation,
  useGetPrioritiesOptionsQuery,
  useGetProjectOptionsLazyQuery,
  useGetStatusOptionsQuery,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit";
import { ModalTaskForm } from "@src/components/widgets/Modals/ModalTask";
import { ModalTask } from "@src/components/widgets/Modals/ModalTask/ModalTask";
import { useAutocompletePositionsFromOurWorkBudgetItem } from "@src/components/widgets/Modals/ModalTask/useAutocompletePositionsFromOurWorkBudgetItem";
import { trackEvent } from "@src/services/amplitude";
import { appStore } from "@src/stores/AppStore";
import { useStore } from "@src/utils/hooks";
import mapToOptions from "@src/utils/map-to-options";
import { FormMode } from "@src/utils/types";
import { generateTaskOptionLabel } from "@src/widgets/TaskSelect/TaskSelect";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";

export const ModalTaskCreate = observer(function ModalTaskCreate() {
  const { UIStore, workspaceStore, taskFormModalStore, authStore } = useStore();
  const { data: statusesResponse } = useGetStatusOptionsQuery({
    skip: !taskFormModalStore.modalState.isOpen,
  });
  const [projectOptions, setProjectOptions] = useState<IOption[]>([]);
  const [priorityOptions, setPriorityOptions] = useState<IOption[]>([]);
  const [taskOptions, setTaskOptions] = useState<IOption[]>([]);
  const [form] = useState(() => new ModalTaskForm(appStore));
  const [createTask, { loading }] = useCreateTaskMutation();
  const [createScopedTask, { loading: scopedLoading }] =
    useCreateScopedTaskMutation();

  useEffect(() => {
    if (!workspaceStore.settings) return;
    const { default_task_priority_id, default_task_status_id } =
      workspaceStore.settings;

    if (!default_task_priority_id || !default_task_status_id) return;

    form.priority.onChange(default_task_priority_id.toString());
    form.status.onChange(default_task_status_id.toString());
  }, [workspaceStore.settings]);

  const statusOptions: IOption[] = useMemo(() => {
    if (!statusesResponse?.taskStatuses) return [];

    return statusesResponse.taskStatuses.map((status) => ({
      value: status.id,
      label: status.name,
    }));
  }, [statusesResponse]);

  const [fetchProjectOptions] = useGetProjectOptionsLazyQuery({
    variables: {
      withTemplates: taskFormModalStore.modalState.additionalData?.isTemplate,
    },
    onCompleted(data) {
      if (data.projectsSimpleMap) {
        setProjectOptions(mapToOptions.projects(data.projectsSimpleMap));
      }
    },
  });

  useGetPrioritiesOptionsQuery({
    skip: !taskFormModalStore.modalState.isOpen,
    onCompleted(data) {
      if (data.taskPriorities) {
        setPriorityOptions(
          mapToOptions.priorities.toOptions(data.taskPriorities),
        );
      }
    },
  });

  const { autocompletePositions } =
    useAutocompletePositionsFromOurWorkBudgetItem(form);

  const onSubmitWrapper = async (hasError: boolean) => {
    if (hasError) return false;
    const input = form.serialize();
    if (authStore.isInternalUser) {
      const { data } = await createTask({
        variables: { input } as CreateTaskMutationVariables,
      });
      if (!data?.createTask) return false;

      taskFormModalStore.modalState.additionalData?.onSubmit?.(data.createTask);
      UIStore.toast({ title: t`Created`, status: "success" });
      trackEvent("task", "Created task");
      return true;
    } else {
      const { data } = await createScopedTask({
        variables: { input } as CreateScopedTaskMutationVariables,
      });
      if (!data?.createScopedTask) return false;

      taskFormModalStore.modalState.additionalData?.onSubmit?.(
        // @ts-expect-error
        data.createScopedTask,
      );
      UIStore.toast({ title: t`Created`, status: "success" });
      trackEvent("task", "Created task");
      return true;
    }
  };

  useGetPrioritiesOptionsQuery({
    onCompleted(data) {
      if (data.taskPriorities) {
        setPriorityOptions(
          mapToOptions.priorities.toOptions(data.taskPriorities),
        );
      }
    },
  });

  useEffect(() => {
    if (!taskFormModalStore.modalState.isOpen) {
      setTaskOptions([]);
      return;
    }
    const { additionalData } = taskFormModalStore.modalState;
    if (!additionalData) return;

    fetchProjectOptions();
    if (additionalData.projectId) {
      form.projectId.onChange(additionalData.projectId);
    }

    if (additionalData.budgetItemId) {
      form.budgetItemId.onChange(additionalData.budgetItemId);
      autocompletePositions({
        variables: {
          our_work_budget_item_id: additionalData.budgetItemId,
        },
      });
    }

    if (additionalData.billable !== undefined) {
      form.billable.onChange(additionalData.billable ? "true" : "false");
    }

    if (additionalData.taskStatusId) {
      form.status.onChange(additionalData.taskStatusId);
    }

    if (additionalData.taskPriorityId) {
      form.priority.onChange(additionalData.taskPriorityId);
    }

    if (additionalData.userId) {
      Array.from(form.positions.values())[0].$.userId.onChange(
        additionalData.userId,
      );
    }

    if (additionalData.parentTask) {
      form.parent_id.onChange(additionalData.parentTask.id);
      setTaskOptions([
        {
          label: generateTaskOptionLabel(additionalData.parentTask),
          value: additionalData.parentTask.id,
        },
      ]);
    }

    form.isTemplate.onChange(
      taskFormModalStore.modalState.additionalData?.isTemplate ?? false,
    );
    form.taskGroupTemplateId.onChange(
      taskFormModalStore.modalState.additionalData?.templateGroupId,
    );
  }, [
    taskFormModalStore.modalState.isOpen,
    taskFormModalStore.modalState.additionalData,
  ]);

  return (
    <ModalTask
      isOpen={taskFormModalStore.modalState.isOpen}
      onClose={taskFormModalStore.modalState.close}
      form={form}
      projectId={taskFormModalStore.modalState.additionalData?.projectId}
      projectOptions={projectOptions}
      priorityOptions={priorityOptions}
      defaultTaskOptions={taskOptions}
      statusOptions={statusOptions}
      mode={FormMode.CREATE}
      onSubmitLoading={loading || scopedLoading}
      onSubmit={onSubmitWrapper}
      filterParentTasksForGroup={
        taskFormModalStore.modalState.additionalData?.filterParentTasksForGroup
      }
    />
  );
});
